/** Today: what to do today (docs/design.md). Hero with the day's session and readiness, the coach's
 * questions, the last ride, routes, tomorrow. Numbers that don't change today's decision live elsewhere. */
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { download } from "@/api/client";
import { addDays, format } from "date-fns";
import {
  useActivities,
  useChooseRoute,
  useDismissRoute,
  useGarminLinked,
  useGenerateRoutes,
  useMe,
  usePhases,
  usePlaces,
  usePlan,
  useRoute,
  useRoutes,
  useSetDayStart,
  useStatus,
} from "@/api/hooks";
import type { Phase, PlannedSession, RouteSummary, Status } from "@/api/schemas";
import {
  CheckinSummary,
  CoachCard,
  RideCheckinForm,
  RideWeather,
  RouteRequestBox,
  SKIP_LABEL,
  SkipDialog,
  WeatherChip,
} from "@/components/Coach";
import { RaceLine } from "@/components/RaceLine";
import { RideSuggestions } from "@/components/RideSuggestions";
import { ElevationProfile } from "@/components/ElevationProfile";
import { CoachChat } from "@/components/CoachChat";
import { IndoorCard } from "@/components/IndoorCard";
import { InsightsBlock } from "@/components/InsightsBlock";
import { AnalysisSummary, ClimbTable } from "@/components/RouteAnalysis";
import { RouteMap } from "@/components/RouteMap";
import { KomootPicker, OpenInKomoot, useKomootConnected } from "@/components/Komoot";
import { Description, SessionCard, SessionEditor } from "@/components/SessionCard";
import { Term } from "@/components/Term";
import { useToast } from "@/components/Toast";
import { Badge, Card, Empty, ErrorBox, Skeleton, useMediaQuery } from "@/components/ui";
import {
  dayLong,
  duration,
  goalName,
  hours,
  kindLabel,
  km,
  meters,
  num,
  pct,
  sportLabel,
  time,
} from "@/lib/format";

const LEVEL = {
  good: { label: "Ready", tone: "good" },
  caution: { label: "Caution", tone: "warn" },
  low: { label: "Recover", tone: "bad" },
  unknown: { label: "No recovery data yet", tone: "" },
} as const;

const RIDING = ["gravel", "cycling", "mtb", "indoor_cycling"];
const OUTDOOR = ["gravel", "cycling", "mtb"];

/** The readiness advice in words that fit the day's session: "Ride as planned" on a hockey day reads wrong. */
export function adviceFor(advice: string, level: string, p: PlannedSession | null): string {
  const rest = !p || p.kind === "rest" || p.target_hours === 0;
  if (rest) return level === "good" ? "Rest as planned." : "Rest day: let your body recover.";
  if (RIDING.includes(p.sport)) return advice;
  const what = kindLabel(p.kind);
  if (advice.startsWith("Ride as planned"))
    return `${what} as planned, but keep it easy: skip any extra intensity today.`;
  if (advice.startsWith("Recovery day"))
    return `Take it easy today: skip the ${what.toLowerCase()} or keep it light.`;
  if (advice === "Train as planned.") return `${what} as planned.`;
  return advice;
}

/** The route climbs far more than the session asks for (the planner couldn't find a flatter loop). */
export function climbMiss(target: number | null | undefined, ascent: number | null | undefined): boolean {
  if (target == null || target <= 0 || ascent == null) return false;
  return ascent > Math.max(target * 1.5, target + 300);
}

/** Kinds the server makes routes for (backend `kinds.ROUTE_KINDS`: FTP tests and sweet spot are trainer rides). */
const ROUTE_KINDS = ["endurance", "long", "back_to_back", "recovery", "tempo_climbs"];

/** The day's ride the server makes routes for, by its own rule (`planning/routes/service.planned_ride`): an outdoor
 * route kind, more than 0 hours, not skipped; the longest. None: "Generate" would make nothing (BR-141). */
export function routableRide(ss: PlannedSession[]): PlannedSession | null {
  const rides = ss.filter(
    (x) =>
      ROUTE_KINDS.includes(x.kind) &&
      OUTDOOR.includes(x.sport) &&
      x.target_hours > 0 &&
      x.status !== "skipped",
  );
  return rides.length ? [...rides].sort((a, b) => b.target_hours - a.target_hours)[0]! : null;
}

/** The day's main session: the longest non-rest one, else the first. */
export function mainSession(ss: PlannedSession[]): PlannedSession | null {
  const active = ss.filter((x) => x.status !== "skipped");
  const work = active.filter((x) => x.kind !== "rest");
  if (work.length) return [...work].sort((a, b) => b.target_hours - a.target_hours)[0]!;
  return active[0] ?? ss[0] ?? null;
}

function SessionLine({ p }: { p: PlannedSession }) {
  const bits: string[] = [];
  if (p.hr_cap != null) bits.push(`HR under ${num(p.hr_cap)}`);
  if (p.power_low_w != null && p.power_high_w != null)
    bits.push(`${num(p.power_low_w)}–${num(p.power_high_w)} W`);
  if (p.target_ascent_m != null) bits.push(`${meters(p.target_ascent_m)} climbing`);
  if (p.min_unpaved_pct != null) bits.push(`at least ${num(p.min_unpaved_pct * 100)}% unpaved`);
  return bits.length ? <p className="hero-targets">{bits.join(", ")}</p> : null;
}

function Hero({
  s,
  phases,
  onEdit,
  onSkip,
  onRoutes,
  routeDay: rDay,
  routeSession,
}: {
  s: Status;
  phases: Phase[];
  onEdit: (p: PlannedSession) => void;
  onSkip: (p: PlannedSession) => void;
  /** absent while the routes section is hidden (a new rider without a start place) */
  onRoutes?: () => void;
  routeDay: string;
  routeSession: PlannedSession | null;
}) {
  const p = mainSession(s.today_sessions);
  const r = s.daily_readiness;
  const lvl = LEVEL[r?.level ?? "unknown"];
  const others = s.today_sessions.filter((x) => x !== p && x.kind !== "rest");
  // a change the coach made to today (a skip of another day is said in the skip dialog, not here)
  const changedToday = s.adjustments.find(
    (a) => a.created_at.slice(0, 10) === s.today && a.kind !== "manual" && a.kind !== "skip",
  );
  const skippedToday = s.today_sessions.filter((x) => x.status === "skipped");
  const rest = !p || p.kind === "rest" || p.target_hours === 0;
  const outdoor = p && ["gravel", "cycling", "mtb"].includes(p.sport) && !rest;
  return (
    <section className="hero" aria-label="Today">
      <div className="hero-head">
        <span>
          {dayLong(s.today)}
          {s.phase && (
            <span className="muted">
              {" "}
              in {s.phase.name}, week {s.phase.week_in_phase} of {s.phase.weeks_in_phase}
            </span>
          )}
        </span>
        <span className="hero-countdown">
          {s.amr_fitness?.score != null && !s.goal?.placeholder && (
            <Link
              to="/progress"
              className="hero-prep"
              title="How ready you are for your race, 1 to 100 (Progress)"
            >
              Race readiness <b>{num(s.amr_fitness.score)}</b>/100
              <span className="sr-only"> for {goalName(s.goal)}</span>
            </Link>
          )}
          {s.goal?.placeholder ? (
            <span data-testid="no-race-yet">No race yet</span>
          ) : (
            s.goal && (
              <span>
                <b>{num(s.goal.days_to_go)}</b> days to {goalName(s.goal)}
              </span>
            )
          )}
        </span>
      </div>
      {s.phase && s.phase.warnings.length > 0 && (
        <ul className="plan-warnings" data-testid="plan-warnings" aria-label="About your plan">
          {s.phase.warnings.map((w) => (
            <li key={w} className="badge warn wrap">
              {w}
            </li>
          ))}
        </ul>
      )}
      {phases.length > 0 && (
        <RaceLine
          phases={phases}
          today={s.today}
          goalName={s.goal?.name}
          goalDate={s.goal?.event_date}
          placeholder={s.goal?.placeholder}
        />
      )}
      <div className="hero-body">
        <div className="hero-session" data-testid="hero-session">
          {rest ? (
            <h1 className="hero-title">Rest day</h1>
          ) : (
            <h1 className="hero-title">
              {kindLabel(p!.kind)} <span className="hero-hours">{hours(p!.target_hours)}</span>
            </h1>
          )}
          {p && !rest && (
            <>
              <SessionLine p={p} />
              <Description text={p.description} className="hero-desc" />
              {outdoor && <WeatherChip w={p.weather} warnings={p.weather_warnings} />}
            </>
          )}
          {others.length > 0 && (
            <p className="muted">
              Also today: {others.map((o) => `${kindLabel(o.kind)} ${hours(o.target_hours)}`).join(", ")}
            </p>
          )}
          {changedToday && <p className="hero-change">{changedToday.reason}</p>}
          {skippedToday.map((x) => (
            <p key={x.id} className="hero-change" data-testid="skipped-today">
              Skipped today: {kindLabel(x.kind)} {hours(x.target_hours)}
              {x.skip_reason ? ` (${SKIP_LABEL[x.skip_reason]})` : ""}. Nothing moves to other days.
            </p>
          ))}
          {s.sync_warning && (
            <p className="hero-change warn" role="status" data-testid="sync-warning">
              {s.sync_warning} <Link to="/settings">Check the connection</Link>
            </p>
          )}
          {p && (
            <div className="row hero-actions">
              {outdoor && onRoutes && (
                <button className="primary" onClick={onRoutes}>
                  Routes for today
                </button>
              )}
              {p.status !== "skipped" && !rest && <button onClick={() => onSkip(p)}>Skip…</button>}
              <button className="ghost" onClick={() => onEdit(p)}>
                Change
              </button>
            </div>
          )}
        </div>
        {/* the day's best route: under the session, across the hero's full width on a desktop (BR-139) */}
        {onRoutes && <TopRoute day={rDay} today={s.today} session={routeSession} onOpen={onRoutes} />}
        {r && r.score != null && (
          <div className={`hero-readiness ${lvl.tone}`} data-testid="readiness">
            <div className="hero-score">
              <b>{r.score ?? "–"}</b>
              <span>
                <Term k="readiness">{lvl.label}</Term>
              </span>
            </div>
            <p>{adviceFor(r.advice, r.level, p)}</p>
            <details>
              <summary>Why</summary>
              <ul className="signals">
                {r.components.map((c) => (
                  <li key={c.key}>
                    <span
                      className={`dot ${c.impact < -10 ? "bad" : c.impact < 0 ? "warn" : "good"}`}
                      aria-hidden
                    />
                    <span>
                      <b>{c.label}</b> {c.value != null ? `${c.value}${c.unit ? ` ${c.unit}` : ""}` : ""}{" "}
                      <span className="muted">{c.note}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          </div>
        )}
      </div>
    </section>
  );
}

/** The day's best route right under the session: "where do I ride?" without scrolling (UX review). */
function TopRoute({
  day,
  today,
  session,
  onOpen,
}: {
  day: string;
  today: string;
  session: PlannedSession | null;
  onOpen: () => void;
}) {
  const routes = useRoutes(session ? day : "");
  if (!session) return null;
  const list = (routes.data ?? []).filter((x) => x.status !== "dismissed");
  // looking never commits (RD-27a, OD-19): only a route the rider confirmed is shown as the day's route
  const r = list.find((x) => x.status === "chosen");
  if (!r) {
    if (list.length === 0) return null;
    return (
      <button type="button" className="top-route" onClick={onOpen} data-testid="top-route">
        <span className="top-route-head">
          <b>No route chosen</b>
          {day !== today && <span className="muted"> · {dayLong(day)}</span>}
        </span>
        <span className="muted">
          {list.length} suggested route{list.length === 1 ? "" : "s"}: choose one with "Ride this".
        </span>
        <span className="top-route-open">Open routes</span>
      </button>
    );
  }
  const hilly = climbMiss(session.target_ascent_m, r.ascent_m);
  return (
    <button type="button" className="top-route" onClick={onOpen} data-testid="top-route">
      <span className="top-route-head">
        <b>{routeTitle(r)}</b>
        {day !== today && <span className="muted"> · {dayLong(day)}</span>}
      </span>
      <span className="muted">
        {num(r.distance_km)} km · {num(r.ascent_m)} m up · {pct(r.unpaved_pct)} unpaved · {hours(r.est_hours)}
      </span>
      {hilly && (
        <small className="warn-text" data-testid="route-hillier">
          Hillier than planned: keep it easy on the climbs.
        </small>
      )}
      <span className="top-route-open">Open routes</span>
    </button>
  );
}

/** The calendar day after a YYYY-MM-DD day. */
export function nextDay(day: string): string {
  return format(addDays(new Date(`${day}T12:00:00`), 1), "yyyy-MM-dd");
}

function Tomorrow({ s, onEdit }: { s: Status; onEdit: (p: PlannedSession) => void }) {
  const t = s.tomorrow_sessions.filter((x) => x.kind !== "rest" && x.status !== "skipped");
  return (
    // with nothing planned tomorrow the date is still tomorrow's, never today's (BR-133)
    <Card title={`Tomorrow, ${dayLong(s.tomorrow_sessions[0]?.day ?? nextDay(s.today))}`}>
      {t.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          Rest.
        </p>
      ) : (
        <div className="list">
          {t.map((x) => (
            <SessionCard key={x.id} s={x} onEdit={onEdit} />
          ))}
        </div>
      )}
    </Card>
  );
}

/** "#1 Loop north" for suggestions; an imported route keeps its own name. */
export function routeTitle(r: RouteSummary): string {
  const name = r.title.split(" · ")[0] ?? r.title;
  return r.imported ? name : `#${r.rank} Loop ${name.replace(/^Loop /, "")}`;
}

function RouteCard({
  r,
  selected,
  onSelect,
  planHours,
  planAscent,
}: {
  r: RouteSummary;
  selected: boolean;
  onSelect: () => void;
  planHours: number | null;
  planAscent?: number | null;
}) {
  return (
    <button
      className={`route-card ${selected ? "selected" : ""}`}
      onClick={onSelect}
      aria-pressed={selected}
      data-testid="route-card"
    >
      <div className="row between">
        <b>{routeTitle(r)}</b>
        {r.status === "chosen" && <Badge tone="good">chosen</Badge>}
      </div>
      <div className="stats">
        <span>
          <b>{num(r.distance_km)}</b>km
        </span>
        <span>
          <b>{num(r.ascent_m)}</b>m up
        </span>
        <span>
          <b>{hours(r.est_hours)}</b>est.
        </span>
      </div>
      {r.imported && planHours != null && Math.abs(r.est_hours - planHours) >= 0.5 && (
        <small className="muted" data-testid="route-vs-plan">
          Your plan for the day: {hours(planHours)}. Riding this route takes about {hours(r.est_hours)}.
        </small>
      )}
      <div className="stats">
        <span>
          <b>{pct(r.unpaved_pct)}</b>unpaved
        </span>
        {r.novelty_pct != null && (
          <span>
            <b>{pct(r.novelty_pct)}</b>new roads
          </span>
        )}
      </div>
      {climbMiss(planAscent, r.ascent_m) && (
        <small className="warn-text">
          Hillier than the plan&apos;s {num(planAscent ?? 0)} m: keep the climbs easy.
        </small>
      )}
      {selected && <small className="muted route-reason">{r.reason}</small>}
    </button>
  );
}

function RouteDetailPane({ id }: { id: string }) {
  const q = useRoute(id);
  const choose = useChooseRoute();
  const dismiss = useDismissRoute();
  const komoot = useKomootConnected();
  const toast = useToast();
  if (q.isLoading) return <Skeleton height={420} />;
  if (q.error && !q.data) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const r = q.data!;
  const w = r.weather;
  return (
    <div className="stack" data-testid="route-detail">
      <RouteMap geometry={r.geometry} segments={r.analysis.segments} label={`Map of ${r.title}`} />
      <div className="row between">
        <div className="row">
          <button
            className="primary small"
            disabled={r.status === "chosen" || choose.isPending}
            onClick={() =>
              choose.mutateAsync(r.id).then(
                () => toast.ok("Route chosen"),
                (e: unknown) => toast.error(e),
              )
            }
          >
            {r.status === "chosen" ? "Chosen" : "Ride this"}
          </button>
          <button
            className="small"
            onClick={() =>
              download(`/routes/${r.id}/gpx`, `${r.day}-${r.rank}.gpx`).catch((e: unknown) => toast.error(e))
            }
          >
            Download GPX
          </button>
          {komoot && <OpenInKomoot url={r.komoot_url} />}
          <button
            className="ghost small"
            onClick={() =>
              dismiss.mutateAsync(r.id).then(
                () => toast.ok("Dismissed"),
                (e: unknown) => toast.error(e),
              )
            }
          >
            Dismiss
          </button>
        </div>
        {w && (
          <span className="muted" style={{ fontSize: 13 }}>
            {num(w.tmin)}–{num(w.tmax)}°C · rain {pct(w.precip_prob)} · wind {num(w.wind_max_kmh)} km/h
            {w.sunset && <> · sunset {time(w.sunset)}</>}
          </span>
        )}
      </div>
      <AnalysisSummary a={r.analysis} />
      <ElevationProfile analysis={r.analysis} />
      {r.analysis.climbs.length > 0 && (
        <details className="climbs">
          <summary>
            {r.analysis.climbs.length} climb{r.analysis.climbs.length === 1 ? "" : "s"}, the biggest{" "}
            {meters(Math.max(...r.analysis.climbs.map((c) => c.gain_m)))}
          </summary>
          <ClimbTable climbs={r.analysis.climbs} />
        </details>
      )}
      {r.notes.length > 0 && (
        <ul className="notes">
          {r.notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NoStartPlace() {
  return (
    <div className="empty stack" style={{ alignItems: "center", gap: 10 }} data-testid="no-start-place">
      <span>Routes start from your start place. Tell us where you usually start your rides.</span>
      <Link className="btn primary" to="/settings#places">
        Set your start place
      </Link>
    </div>
  );
}

/** "Today, Wed 4 Nov" / "Tomorrow, Thu 5 Nov" / "Fri 6 Nov": the routes block always names its day. */
export function routesDayLabel(day: string, today?: string): string {
  const date = format(new Date(`${day}T12:00:00`), "EEE d MMM");
  if (today && day === today) return `Today, ${date}`;
  if (today && day === nextDay(today)) return `Tomorrow, ${date}`;
  return date;
}

export function RoutesSection({
  day,
  today,
  focusRequest,
  planHours,
  planAscent,
}: {
  day: string;
  today?: string;
  focusRequest: boolean;
  planHours: number | null;
  planAscent?: number | null;
}) {
  const routes = useRoutes(day);
  const places = usePlaces();
  const gen = useGenerateRoutes();
  const setStart = useSetDayStart();
  const toast = useToast();
  const [picked, setPicked] = useState<string | null>(null);
  const list = (routes.data ?? []).filter((r) => r.status !== "dismissed");
  const selected =
    picked && list.some((r) => r.id === picked)
      ? picked
      : ((list.find((r) => r.status === "chosen") ?? list[0])?.id ?? null);
  const startName = list[0]?.start?.name;
  const noStart = places.data !== undefined && places.data.length === 0 && list.length === 0;

  async function regenerate(place_id?: string) {
    try {
      if (place_id !== undefined) await setStart.mutateAsync({ day, place_id: place_id || null });
      const r = await gen.mutateAsync({ day, place_id: place_id || undefined });
      // the server makes routes only for a planned outdoor ride (BR-141): never a silent "0 routes"
      if (r.length) toast.ok(`${r.length} routes generated`);
      else if (place_id !== undefined)
        toast.ok(
          "Start place saved. No outdoor ride is planned for this day: describe the ride you want above.",
        );
      else toast.ok("No outdoor ride is planned for this day: describe the ride you want above.");
    } catch (e) {
      toast.error(e);
    }
  }

  return (
    <Card
      title={`Routes · ${routesDayLabel(day, today)}`}
      actions={
        <>
          {places.data && places.data.length > 1 && (
            <select
              aria-label="Start point"
              style={{ width: "auto" }}
              value=""
              onChange={(e) => void regenerate(e.target.value)}
              disabled={gen.isPending}
            >
              <option value="">Start: {startName ?? "home"}</option>
              {places.data.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
          <KomootPicker day={day} />
          {/* no outdoor ride planned: "Generate" would make nothing; the request box above adds the ride (BR-141) */}
          {!noStart && planHours != null && (
            <button className="small" onClick={() => void regenerate()} disabled={gen.isPending}>
              {gen.isPending ? "Routing… (~1 min)" : list.length ? "Regenerate" : "Generate"}
            </button>
          )}
        </>
      }
    >
      {/* without a start place there is one next step: set it (the request box would only fail, BR-134) */}
      {!noStart && (
        <div id="route-request" style={{ marginBottom: 14 }}>
          <RouteRequestBox key={`${day}-${focusRequest}`} day={day} autoFocus={focusRequest} />
        </div>
      )}
      {routes.isLoading ? (
        <Skeleton height={300} />
      ) : noStart ? (
        <NoStartPlace />
      ) : routes.error && !routes.data ? (
        <ErrorBox error={routes.error} retry={() => routes.refetch()} />
      ) : list.length === 0 ? (
        <Empty>
          {planHours != null
            ? "No routes for this ride yet. Generate three loops from your start place, or describe the route you want."
            : "No outdoor ride is planned for this day. To ride anyway, describe it above (for example “2 h, mostly gravel”) and you get three routes."}
        </Empty>
      ) : (
        <div className="grid routes">
          <div className="list">
            {list.map((r) => (
              <RouteCard
                key={r.id}
                r={r}
                selected={r.id === selected}
                onSelect={() => setPicked(r.id)}
                planHours={planHours}
                planAscent={planAscent}
              />
            ))}
          </div>
          {selected && <RouteDetailPane id={selected} />}
        </div>
      )}
    </Card>
  );
}

function LastTraining({ s }: { s: Status }) {
  const a = s.last_activity;
  // the coach card already asks about this ride: one place to answer
  const asked = !!a && s.inbox.some((it) => it.kind === "ride_checkin" && it.activity_id === a.id);
  const garmin = useGarminLinked();
  const [editing, setEditing] = useState(false);
  if (!a) {
    return (
      <Card title="Last training">
        {garmin ? (
          <Empty>No training in the last 7 days. New Garmin activities appear here within 15 minutes.</Empty>
        ) : (
          <div className="empty stack" style={{ alignItems: "center", gap: 10 }} data-testid="no-training">
            <span>No training in the last 7 days. Connect Garmin, or upload a ride, to see it here.</span>
            <Link className="btn primary" to="/settings#data">
              Connect Garmin or upload
            </Link>
          </div>
        )}
      </Card>
    );
  }
  return (
    <Card title="Last training" actions={<Link to={`/activities/${encodeURIComponent(a.id)}`}>Details</Link>}>
      <div className="stack" style={{ gap: 10 }} data-testid="last-training">
        <div>
          <b>{a.name || sportLabel(a.sport)}</b>{" "}
          <span className="muted">
            · {dayLong(a.start_time)} {time(a.start_time)}
          </span>
          <div className="kpi-row" style={{ marginTop: 4 }}>
            <span>
              <b>{duration(a.moving_s ?? a.elapsed_s)}</b>
            </span>
            {a.distance_m != null && (
              <span>
                <b>{km(a.distance_m / 1000, 1)}</b>
              </span>
            )}
            {a.ascent_m != null && (
              <span>
                <b>{meters(a.ascent_m)}</b>
              </span>
            )}
            {a.avg_hr != null && (
              <span>
                HR <b>{num(a.avg_hr)}</b>
              </span>
            )}
            {(a.tss ?? a.hr_tss) != null && (
              <span>
                <Term k="load">Load</Term> <b>{num(a.tss ?? a.hr_tss)}</b>
              </span>
            )}
          </div>
          <RideWeather w={a.weather} />
        </div>
        {a.checkin && !editing ? (
          <div className="row between" style={{ alignItems: "flex-start" }}>
            <CheckinSummary c={a.checkin} />
            <button className="ghost small" onClick={() => setEditing(true)}>
              Edit
            </button>
          </div>
        ) : asked && !editing ? (
          <small className="muted">The coach asks about this ride above.</small>
        ) : (
          (editing || a.elapsed_s >= 1800) && (
            <details open={editing}>
              <summary>How was it? (10 seconds)</summary>
              <RideCheckinForm
                activityId={a.id}
                initial={a.checkin}
                hours={(a.moving_s ?? a.elapsed_s) / 3600}
                onDone={() => setEditing(false)}
              />
            </details>
          )
        )}
        <InsightsBlock i={a.insights} />
      </div>
    </Card>
  );
}

type Step = { key: string; label: string; done: boolean; to: string; action: string; optional?: boolean };

/** What a new rider still has to do: race, start place, first rides, (optional) health data. */
export function firstRunSteps(v: {
  hasGoal: boolean;
  hasPlace: boolean;
  hasRides: boolean;
  healthData: boolean;
}): Step[] {
  return [
    { key: "race", label: "Pick your race", done: v.hasGoal, to: "/welcome", action: "Pick" },
    {
      key: "place",
      label: "Set where your rides start",
      done: v.hasPlace,
      to: "/settings#places",
      action: "Set start place",
    },
    {
      key: "rides",
      label: "Bring in your rides (connect Garmin, or upload its export or FIT files)",
      done: v.hasRides,
      to: "/settings#data",
      action: "Add rides",
    },
    {
      key: "health",
      label: "Use your sleep, HRV and resting heart rate (optional)",
      done: v.healthData,
      to: "/settings",
      action: "Turn on",
      optional: true,
    },
  ];
}

/** "2 steps left, 1 optional": the count matches the rows the rider sees (BR-134). */
export function stepsLeft(steps: Step[]): string {
  const left = steps.filter((x) => !x.done && !x.optional).length;
  const optional = steps.filter((x) => !x.done && x.optional).length;
  const head = left === 1 ? "1 step left" : `${left} steps left`;
  return optional ? `${head}, ${optional} optional` : head;
}

/** "Your plan is ready: first ride Thu 29 Oct, Endurance 1 h" and the week ahead, for a rider without rides. */
export function planLead(
  plan: PlannedSession[],
  today: string,
): { first: PlannedSession | null; week: PlannedSession[] } {
  const open = plan.filter(
    (x) => x.day >= today && x.status !== "skipped" && x.kind !== "rest" && x.target_hours > 0,
  );
  const end = format(addDays(new Date(`${today}T12:00:00`), 7), "yyyy-MM-dd");
  return {
    first: open.find((x) => RIDING.includes(x.sport)) ?? null,
    week: open.filter((x) => x.day < end),
  };
}

function FirstRun({ s }: { s: Status }) {
  const me = useMe();
  const places = usePlaces();
  const rides = useActivities({ limit: 1 });
  const to = format(addDays(new Date(`${s.today}T12:00:00`), 14), "yyyy-MM-dd");
  const plan = usePlan(s.today, to);
  if (!me.data || !places.data || !rides.data) return null;
  const steps = firstRunSteps({
    hasGoal: !!s.goal,
    hasPlace: places.data.length > 0,
    hasRides: rides.data.total > 0,
    healthData: me.data.health_data,
  });
  if (steps.every((x) => x.done || x.optional)) return null;
  const lead = rides.data.total === 0 && plan.data ? planLead(plan.data, s.today) : null;
  return (
    <Card title="Get started">
      {lead?.first && (
        <div className="stack" style={{ gap: 6, marginBottom: 12 }} data-testid="plan-lead">
          <p style={{ margin: 0 }}>
            <b>Your plan is ready:</b> first ride {dayLong(lead.first.day)}, {kindLabel(lead.first.kind)}{" "}
            {hours(lead.first.target_hours)}.
          </p>
          {lead.week.length > 1 && (
            <small className="muted">
              This week:{" "}
              {lead.week
                .map((x) => `${dayLong(x.day).split(" ")[0]} ${kindLabel(x.kind)} ${hours(x.target_hours)}`)
                .join(" · ")}
              . It starts gently and builds from week to week.
            </small>
          )}
        </div>
      )}
      <p className="muted" style={{ marginTop: 0 }}>
        {stepsLeft(steps)}. Then the coach knows you well.
      </p>
      <ol className="checklist" data-testid="first-run">
        {steps.map((x) => (
          <li key={x.key} className={x.done ? "done" : ""}>
            <span className="tick" aria-hidden>
              {x.done ? "✓" : ""}
            </span>
            <span className="what">
              {x.label}
              <span className="sr-only">{x.done ? " (done)" : " (to do)"}</span>
            </span>
            {!x.done && (
              <Link to={x.to} className="nowrap">
                {x.action}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </Card>
  );
}

/** First upcoming day (today or tomorrow) with an outdoor ride, for the routes section. */
export function routeDay(s: Status): string {
  // the same rule as the routes card and the server (routableRide): a day with a ride that gets routes
  if (routableRide(s.today_sessions)) return s.today;
  if (routableRide(s.tomorrow_sessions) && s.tomorrow_sessions[0]) return s.tomorrow_sessions[0].day;
  return s.today;
}

const ROUTES_OPEN = "bason.today.routes-open";
function readRoutesOpen(): boolean {
  try {
    return window.sessionStorage.getItem(ROUTES_OPEN) === "1";
  } catch {
    return false;
  }
}
function saveRoutesOpen(open: boolean) {
  try {
    window.sessionStorage.setItem(ROUTES_OPEN, open ? "1" : "0");
  } catch {
    /* blocked storage: the fold just doesn't remember */
  }
}

export function TodayPage() {
  const q = useStatus();
  const phases = usePhases();
  const meQ = useMe();
  const rides = useActivities({ limit: 1 });
  const [editing, setEditing] = useState<PlannedSession | null>(null);
  const [skipping, setSkipping] = useState<PlannedSession | null>(null);
  const [requestFocus, setRequestFocus] = useState(false);
  const places = usePlaces();
  // phones: the routes block (map, profile, three loops) is folded until asked for; remembered per session (BR-139)
  const navigate = useNavigate();
  const narrow = useMediaQuery("(max-width: 899px)");
  const [routesOpen, setRoutesOpen] = useState(() => readRoutesOpen());
  const openRoutes = (open: boolean) => {
    setRoutesOpen(open);
    saveRoutesOpen(open);
  };
  // after the fold opens, the section exists: scroll on the next frame
  const later = (fn: () => void) =>
    typeof requestAnimationFrame === "function" ? requestAnimationFrame(fn) : setTimeout(fn, 0);
  const toRoutes = () => {
    openRoutes(true);
    later(() => document.getElementById("routes")?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  const describeRoute = () => {
    // without a start place there is no request box: the one next step is to set it (review of G4b)
    if (places.data?.length === 0) return navigate("/settings#places");
    openRoutes(true);
    setRequestFocus(true);
    later(() =>
      document.getElementById("route-request")?.scrollIntoView({ behavior: "smooth", block: "center" }),
    );
  };
  if (q.isLoading) return <Skeleton height={500} />;
  if (q.error && !q.data) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const s = q.data!;
  const rDay = routeDay(s);
  const indoorToday = s.today_sessions.some((x) => x.sport === "indoor_cycling" && x.status !== "skipped");
  const rSessions = rDay === s.today ? s.today_sessions : s.tomorrow_sessions;
  const rMain = mainSession(rSessions.filter((x) => OUTDOOR.includes(x.sport)));
  const rRide = routableRide(rSessions); // what the routes card offers routes for (BR-141)
  const features = meQ.data?.features;
  const newRider = rides.data?.total === 0;
  // a new rider sees no empty cards: "Get started" lists the next steps (no rides yet, no start place) (BR-139)
  const hideRoutes = newRider && (places.isLoading || places.data?.length === 0); // no flash while places load
  const routesLabel = rDay === s.today ? "Routes for today" : `Routes for ${dayLong(rDay)}`;
  return (
    <div className="stack today">
      {newRider && <FirstRun s={s} />}
      <Hero
        s={s}
        phases={phases.data ?? []}
        onEdit={setEditing}
        onSkip={setSkipping}
        onRoutes={hideRoutes ? undefined : toRoutes}
        routeDay={rDay}
        routeSession={rMain}
      />
      {!newRider && <FirstRun s={s} />}
      <RideSuggestions />
      {newRider ? (
        <CoachCard
          inbox={s.inbox}
          today={s.today}
          checkinToday={s.checkin_today}
          lastActivity={s.last_activity}
          onDescribeRoute={describeRoute}
        />
      ) : (
        <div className="grid two">
          <CoachCard
            inbox={s.inbox}
            today={s.today}
            checkinToday={s.checkin_today}
            lastActivity={s.last_activity}
            onDescribeRoute={describeRoute}
          />
          <LastTraining s={s} />
        </div>
      )}
      {features?.ai && <CoachChat />}
      {indoorToday && <IndoorCard day={s.today} />}
      {!hideRoutes && (
        <div id="routes">
          {narrow && (
            <button
              type="button"
              className="routes-fold"
              aria-expanded={routesOpen}
              aria-controls={routesOpen ? "routes-body" : undefined}
              onClick={() => openRoutes(!routesOpen)}
              data-testid="routes-fold"
            >
              {routesLabel}
            </button>
          )}
          {(!narrow || routesOpen) && (
            <div id="routes-body">
              <RoutesSection
                day={rDay}
                today={s.today}
                focusRequest={requestFocus}
                planHours={rRide?.target_hours ?? null}
                planAscent={rRide?.target_ascent_m ?? null}
              />
            </div>
          )}
        </div>
      )}
      <Tomorrow s={s} onEdit={setEditing} />
      <SessionEditor session={editing} onClose={() => setEditing(null)} onDescribeRoute={describeRoute} />
      <SkipDialog session={skipping} onClose={() => setSkipping(null)} onDescribeRoute={describeRoute} />
    </div>
  );
}
